import "./App.css";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import CommonHeader from "./layouts/header";
import AssetsPage from "./pages/investments/assets";
import LayoutBody from "./layouts/content";
import React, { lazy, Suspense, useEffect, useState } from "react";
import { debounce } from "lodash";
import {
  coingeckoDefaultIds,
  countriesDetails,
  defaultVerifiedUrl,
  maximumScreenForMobile,
  paraswapTokensBaseUrl,
  paraswapUnsupportedChains,
  PoolType,
} from "./utils/constants";
import CommonFooter from "./layouts/footer";
import GeneralPopupBackground from "./components/popups";
import { useConnectWallet } from "./hooks/connectWallet";
import { useGetAllPools } from "./hooks/pools";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import MobileKYCPage from "./pages/dashboard/mobileKyc.jsx";
import { useGetSupportedCurrencies } from "./hooks/currencies.js";
import ErrorPage from "./pages/error/error404.jsx";
import unitedStatesFlag from "./assests/page/Flag-United-States-of-America 1.svg";
import MobileLoadingPage from "./pages/payments/mobileLoading.jsx";
import useNetworkStore from "./store/networkStore.js";
import useUserStore from "./store/userSlice.js";
import { useHandleWalletRedirections } from "./hooks/walletActivities.js";
import { useHandleBankAndWalletRedirections } from "./hooks/bankAndCardActivities.js";
import RouteChangeTracker from "./analytics.jsx";
import { ChainConfig, chainDetails } from "./utils/config.js";
import { onMessage } from "firebase/messaging";
import { useSubstreams } from "./hooks/substream.js";
import { getUserKyc, initFireBase } from "./utils/helpers/firebase/index.js";
import { createAccessTokenForNewUser } from "./utils/helpers/kyc/index.js";
import {
  decryptKycQuestionaire,
  decryptUserKycData,
  saveUserKycQuestionaire,
  uploadProductAndSiteMap,
} from "./utils/helpers/utils/index.js";
import { hashTheString } from "@verified-network/verified-custody";
import { contractAddress } from "@verified-network/verified-sdk";
import { initializeI18n } from "./lib/i18n/index.jsx";
import StartupLanguagePage from "./pages/ftu/language.jsx";
import { useTranslation } from "react-i18next";

const DashboardPage = lazy(() => import("./pages/dashboard"));
const OrdersPage = lazy(() => import("./pages/investments/orders"));
const PortfolioPage = lazy(() => import("./pages/investments/portfolio"));
const CreateSecondary = lazy(
  () => import("./pages/investments/createSecondary"),
);
const NewIssue = lazy(() => import("./pages/investments/newIssue"));
const CoporateActions = lazy(
  () => import("./pages/investments/corporateActions"),
);
const WatchlistPage = lazy(() => import("./pages/investments/watchlist"));
const LoansPage = lazy(() => import("./pages/loans/index"));
const MobileDashboardPage = lazy(() => import("./pages/dashboard/mobile"));
const MobileLoansPage = lazy(() => import("./pages/loans/mobile"));
const MobileAssetPage = lazy(() => import("./pages/investments/mobile.jsx"));
const MobilePaymentsPage = lazy(
  () => import("./pages/payments/mobileIndex.jsx"),
);
const MobileConvertFunds = lazy(
  () => import("./pages/payments/mobileConvertFunds.jsx"),
);
const MobileWithdrawFundsPage = lazy(
  () => import("./pages/payments/mobileWithdrawFunds.jsx"),
);
const MobileProfilePage = lazy(() => import("./pages/profile/mobile.jsx"));
const MobileProfileDetailsPage = lazy(
  () => import("./pages/profile/mobileProfileDetails.jsx"),
);
const MobileProfileSettingsPage = lazy(
  () => import("./pages/profile/mobileProfileSettings.jsx"),
);
const MobileAddFundsPage = lazy(
  () => import("./pages/payments/mobileAddFunds.jsx"),
);
const MobileSelectCurrencies = lazy(
  () => import("./pages/payments/mobileCurrency.jsx"),
);
const MobileAssetInfoPage = lazy(
  () => import("./pages/investments/mobileAssetInfo.jsx"),
);
const MobileLinkBankOption = lazy(
  () => import("./pages/dashboard/mobileLinkBankOption.jsx"),
);
const MobileLinkBank = lazy(
  () => import("./pages/dashboard/mobileLinkBank.jsx"),
);
const MobileSelectBank = lazy(
  () => import("./pages/dashboard/mobileSelectBank.jsx"),
);
const MobileSendFundsPage = lazy(
  () => import("./pages/payments/mobileSendFunds.jsx"),
);

function App(props) {
  const { t } = useTranslation();
  const [mobileScreen, setMobileScreen] = useState(
    window.innerWidth <= maximumScreenForMobile,
  );
  const [isFTU, setIsFTU] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [customLoading, setCustomLoading] = useState(false);
  const [popupContent, setPopupContent] = useState(null);
  const [popupContentPosition, setPopupContentPosition] = useState("center");
  const [favouriteClicked, setFavouriteClicked] = useState(false);
  const [expandedView, setExpandedView] = useState(false);
  const [generalRefresh, setGeneralRefresh] = useState(false);
  const [allCountries, setAllCountries] = useState({});
  const [productsToSearch, setProductsToSearch] = useState([]);
  const [pageAction, setPageAction] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState(
    props.currencySelected || {
      countryName: "",
      officialName: "",
      flag: null,
      currencySymbol: "",
      currencyName: "",
      officialCurrencySymbol: "",
      isClicked: false,
      balance: 0.0,
      tokenAddress: "",
    },
  );
  const [selectedCountry, setSelectedCountry] = useState({
    area: "1",
    flag: unitedStatesFlag,
    name: "United States",
    continent: "north america",
    code: "US",
  });
  const [selectedBank, setSelectedBank] = useState({
    name: "",
    acct: "",
    logo: "",
  });
  const [currencyClicked, setCurrencyClicked] = useState("");
  const [pageStep, setPageStep] = useState("1");
  const [isReloadStep, setIsReloadStep] = useState(false);
  const [pageData, setPageData] = useState([]);
  const [selectedCurrencyTo, setSelectedCurrencyTo] = useState({
    area: "1",
    flag: unitedStatesFlag,
    name: "United States",
    continent: "north america",
    code: "US",
  });
  const [productClicked, setProductClicked] = useState({});
  const [showPandL, setShowPandL] = useState(false);
  const [paraswapSupportedTokens, setParaswapSupportedTokens] = useState([]);
  const [verifiedWalletDetails, setVerifiedWalletDetails] = useState({});
  const [verifiedWalletDetailsInt, setVerifiedWalletDetailsInt] = useState({});
  const [custodyWalletDetails, setCustodyWalletDetails] = useState({});
  const [allTokensIdCoinecko, setAllTokensIdCoinecko] = useState([]);
  const [kycQuestionaire, setKycQuestionaire] = useState({});
  const [pendingQuestionaire, setPendingQuestionaire] = useState({});
  const [refreshQuestionaire, setRefereshQuestionaire] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      try {
        const language = await initializeI18n();
        const ftuStatus = localStorage.getItem("VWA:ftu");
        if (!ftuStatus || ftuStatus === "0") {
          setPopupContent(
            <StartupLanguagePage
              defaultLanguage={language}
              setPopupContent={setPopupContent}
              setPopupContentPosition={setPopupContentPosition}
              setPageAction={setPageAction}
              setShowPopup={setShowPopup}
              isMobile={mobileScreen}
            />,
          );
          setPopupContentPosition("center");
          setPageAction("select-language");
          setShowPopup(true);
        }
      } catch (error) {
        console.error("i18n initialization error:", error);
        setPopupContent(null);
        setPopupContentPosition("center");
        setPageAction("");
        setShowPopup(false);
      }
    };

    initialize();
  }, [
    mobileScreen,
    setPageAction,
    setPopupContent,
    setPopupContentPosition,
    setShowPopup,
  ]);

  const { connectWalletLoading, connectWalletError } = useConnectWallet(
    mobileScreen,
    pageAction,
    setShowPopup,
    setPopupContentPosition,
    setPopupContent,
    setFavouriteClicked,
    setCustomLoading,
    setVerifiedWalletDetails,
    verifiedWalletDetails,
    custodyWalletDetails,
    verifiedWalletDetailsInt,
    setVerifiedWalletDetailsInt,
  );

  const { getSupportedCurrenciesData } = useGetSupportedCurrencies();

  const { chainId, name } = useNetworkStore();
  const { userAddress, userKycStatus, isConnected, userCountry, userType } =
    useUserStore();

  if (connectWalletError) {
    console.log("connect wallet error: ", connectWalletError);
  }

  const tUrl = (text) => {
    return t(text)?.replaceAll(" ", "-")?.toLowerCase();
  };

  const {
    getAllPoolsData,
    getAllPoolsLoading,
    getAllPoolsError,
    getAllPoolsBlock,
  } = useGetAllPools(
    generalRefresh,
    userAddress,
    userCountry,
    userType,
    verifiedWalletDetails,
    custodyWalletDetails,
    true,
    allTokensIdCoinecko,
  );

  let primarySubstreams = {};
  let secondarySubstreams = {};
  let marginSubstreams = {};

  // const { messages: primarySubstreams, connected: primaryConnected } =
  //   useSubstreams(
  //     chainDetails[chainId]?.substreams?.primary?.substreamEndpoint,
  //     chainDetails[chainId]?.substreams?.primary?.substreamToken,
  //     chainDetails[chainId]?.substreams?.primary?.substreamSpkg,
  //     chainDetails[chainId]?.substreams?.primary?.substreamModule,
  //     Number(getAllPoolsBlock),
  //     chainDetails[chainId]?.substreams?.primary?.substreamProto,
  //     chainDetails[chainId]?.substreams?.primary?.substreamMessage,
  //     PoolType.primaryPool
  //   );

  // const { messages: secondarySubstreams, connected: secondaryConnected } =
  //   useSubstreams(
  //     chainDetails[chainId]?.substreams?.secondary?.substreamEndpoint,
  //     chainDetails[chainId]?.substreams?.secondary?.substreamToken,
  //     chainDetails[chainId]?.substreams?.secondary?.substreamSpkg,
  //     chainDetails[chainId]?.substreams?.secondary?.substreamModule,
  //     Number(getAllPoolsBlock),
  //     chainDetails[chainId]?.substreams?.secondary?.substreamProto,
  //     chainDetails[chainId]?.substreams?.secondary?.substreamMessage,
  //     PoolType.secondaryPool
  //   );

  // const { messages: marginSubstreams, connected: marginConnected } =
  //   useSubstreams(
  //     chainDetails[chainId]?.substreams?.margin?.substreamEndpoint,
  //     chainDetails[chainId]?.substreams?.margin?.substreamToken,
  //     chainDetails[chainId]?.substreams?.margin?.substreamSpkg,
  //     chainDetails[chainId]?.substreams?.margin?.substreamModule,
  //     Number(getAllPoolsBlock),
  //     chainDetails[chainId]?.substreams?.margin?.substreamProto,
  //     chainDetails[chainId]?.substreams?.margin?.substreamMessage,
  //     PoolType.marginPool
  //   );

  const generalDataProps = {
    connectWalletLoading,
    getAllPoolsData,
    getAllPoolsLoading,
    getAllPoolsError,
    generalRefresh,
    allCountries,
    paraswapSupportedTokens,
    pageAction,
    pageStep,
    pageData,
    verifiedWalletDetails,
    setVerifiedWalletDetails,
    setPageData,
    selectedCountry,
    selectedBank,
    isReloadStep,
    setIsReloadStep,
    setSelectedBank,
    setSelectedCountry,
    setPageStep,
    setPageAction,
    setGeneralRefresh,
    setProductsToSearch,
    accessToken,
    setAccessToken,
    selectedCurrency,
    setSelectedCurrency,
    selectedCurrencyTo,
    setSelectedCurrencyTo,
    currencyClicked,
    setCurrencyClicked,
    productClicked,
    setProductClicked,
    showPandL,
    setShowPandL,
    setCustodyWalletDetails,
    custodyWalletDetails,
    verifiedWalletDetailsInt,
    setVerifiedWalletDetailsInt,
    coingeckoDefaultIds,
    primarySubstreams,
    secondarySubstreams,
    marginSubstreams,
    setKycQuestionaire,
    kycQuestionaire,
    pendingQuestionaire,
    setRefereshQuestionaire,
    refreshQuestionaire,
  };

  useHandleWalletRedirections(
    mobileScreen,
    setVerifiedWalletDetails,
    setCustodyWalletDetails,
    setPopupContentPosition,
    setPopupContent,
    setShowPopup,
    setPageAction,
  );

  useEffect(() => {
    const resizeScreen = debounce(() => {
      setMobileScreen(window.innerWidth <= maximumScreenForMobile);
    }, 400); //4milliseconds

    //subcribe to resize
    window.addEventListener("resize", resizeScreen);

    //unsubscribe
    return () => {
      window.removeEventListener("resize", resizeScreen);
    };
  }, []);

  useEffect(() => {
    if (
      !userAddress ||
      userAddress?.length === 0 ||
      !name ||
      name?.length === 0 ||
      Number(userKycStatus) < 0
    )
      return;

    const handlePendingKyc = async () => {
      setPendingQuestionaire({});
      const localStorageKey = `${userAddress.toLowerCase()}-kyc-aml`;
      const kycQuestionaireLocal =
        JSON.parse(localStorage.getItem(localStorageKey)) || {};

      if (kycQuestionaireLocal && Number(userKycStatus) === 3) {
        console.log("KYC complete; removing local storage copy");
        localStorage.removeItem(localStorageKey);
      }

      if (Number(userKycStatus) !== 3) {
        let kycQuestionaireDB = {};
        kycQuestionaireDB = await getUserKyc(
          hashTheString(userAddress),
          hashTheString(""),
        );

        kycQuestionaireDB = await getUserKyc(
          hashTheString(userAddress),
          hashTheString(""),
        );

        const dbExists =
          kycQuestionaireDB && Object.keys(kycQuestionaireDB).length > 3;
        const localExists =
          kycQuestionaireLocal && Object.keys(kycQuestionaireLocal).length > 0;

        if (dbExists) {
          const isDBNewer =
            Number(kycQuestionaireDB.lastUpdated || 0) >
            Number(kycQuestionaireLocal.lastUpdated || 0);

          if (!isDBNewer && localExists) {
            setPendingQuestionaire({
              ...kycQuestionaireDB,
              ...kycQuestionaireLocal,
            }); //update outdated values on DB
          } else {
            setPendingQuestionaire({
              ...kycQuestionaireDB,
              shouldSave: {
                notSave: true,
              },
            });
          }
        } else if (localExists) {
          setPendingQuestionaire(kycQuestionaireLocal);
        }
      }
    };

    handlePendingKyc();
  }, [chainId, userAddress, userKycStatus, refreshQuestionaire, name]);

  useEffect(() => {
    const createAccessToken = async () => {
      setAccessToken("");
      const accessToken = await createAccessTokenForNewUser(
        userAddress,
        chainId,
      ).then((res) => res?.accessToken);
      setAccessToken(accessToken);
    };

    if (
      isConnected &&
      chainId &&
      userAddress.length > 0 &&
      Number(userKycStatus) !== 3
    ) {
      createAccessToken();
    }
  }, [userAddress, userKycStatus, isConnected, chainId]);

  useEffect(() => {
    const handleUpdateSiteMap = async (data) => {
      const productsToSend = data
        .filter((dt) => !dt?.securityData?.subscriptionsClosed?.length)
        .map((dt) => ({
          id: dt?.id,
          name: `${dt?.securityName} (${dt?.securitySymbol})`,
          currency: dt?.currencySymbol,
          category: dt?.securityCategory,
          tokenPair: `${dt?.securitySymbol}/${dt?.currencySymbol}`,
          price: dt?.currentPrice,
          createdDate: dt?.createTimeFmt,
          tags: [
            dt?.securityName,
            dt?.securitySymbol,
            dt?.currencySymbol,
            dt?.currencyName,
            `${dt?.securitySymbol}/${dt?.currencySymbol}`,
            dt?.categories,
          ]?.join(", "),
          description: dt?.offeringDocData?.Business?.Summary || "",
          image: dt?.logo || "",
          tokenLink: `${
            ChainConfig[chainDetails[chainId]?.chainId]?.blockExplorerUrls[0]
          }address/${dt?.security}`,
          verifiedLink: `${defaultVerifiedUrl}/investments/assets/${dt?.id}?chainId=${chainDetails[chainId]?.chainId}`,
        }))
        .filter((item) => item.id);

      await uploadProductAndSiteMap(
        productsToSend,
        chainDetails[chainId]?.chainId,
      );
    };
    if (
      getAllPoolsData?.length > 0 &&
      !getAllPoolsLoading &&
      process.env.REACT_APP_NODE_ENV === "production"
    ) {
      handleUpdateSiteMap(getAllPoolsData);
    }
  }, [chainId, getAllPoolsData, getAllPoolsData?.length, getAllPoolsLoading]);

  useHandleBankAndWalletRedirections(
    mobileScreen,
    setPopupContentPosition,
    setPopupContent,
    setFavouriteClicked,
    setShowPopup,
    setPageAction,
    generalDataProps,
    customLoading,
    setCustomLoading,
    setPageStep,
    verifiedWalletDetails,
    custodyWalletDetails,
  );

  useEffect(() => {
    const getAndFormatAllCountries = async () => {
      let allCountriesObj = {};
      // eslint-disable-next-line array-callback-return
      countriesDetails.map((cnt) => {
        const details = {
          name: cnt?.name,
          countryName: cnt?.name,
          officialName: cnt?.name,
          currencySymbol: cnt?.currency,
          currencyName: cnt?.currency,
          officialCurrencySymbol: cnt?.currency,
          countryCode: cnt?.code,
          flag: cnt?.flag,
          continent: cnt?.continent,
          phone_code: cnt?.phone_code,
          code: cnt?.code,
        };
        allCountriesObj[cnt?.code] = details;
      });
      setAllCountries(allCountriesObj);
    };

    getAndFormatAllCountries();
  }, []);

  useEffect(() => {
    const getParaswapSupportedTokens = async () => {
      await axios({ method: "GET", url: `${paraswapTokensBaseUrl}/${chainId}` })
        .then((res) => {
          const supportedParaswapTokens = res?.data?.tokens?.filter((tk) =>
            getSupportedCurrenciesData.find(
              (stk) =>
                stk?.tokenAddress?.toLowerCase() === tk?.address?.toLowerCase(),
            ),
          );
          setParaswapSupportedTokens(supportedParaswapTokens);
        })
        .catch((err) => {
          console.error(
            "getting paraswap tokens for chainId: ",
            chainId,
            "failed with error: ",
            err?.message,
          );
        });
    };

    if (
      chainId > 0 &&
      userAddress &&
      getSupportedCurrenciesData?.length > 0 &&
      !paraswapUnsupportedChains.includes(chainId)
    ) {
      getParaswapSupportedTokens();
    }
  }, [chainId, getSupportedCurrenciesData, userAddress]);

  useEffect(() => {
    // Safari / unsupported browser check
    const pushSupported =
      "Notification" in window && "serviceWorker" in navigator;

    if (!pushSupported) {
      console.warn("Push notifications not supported on this browser.");
      return;
    }

    const { messaging } = initFireBase();

    Notification.requestPermission().then(async (permission) => {
      if (permission !== "granted") {
        console.warn("Notification permission denied.");
        return;
      }

      console.log("Notification permission granted.");

      try {
        await navigator.serviceWorker.register("/firebase-messaging-sw.js");
      } catch (error) {
        console.warn("Service Worker registration failed:", error);
      }
    });

    const unsubscribe = onMessage(messaging, (payload) => {
      if (!("Notification" in window)) return;

      if (payload.notification) {
        new Notification(payload.notification.title ?? "Notification", {
          body: payload.notification.body ?? "",
        });
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const startAllPoolsSubstreams = async () => {};

    if (getAllPoolsBlock && Number(getAllPoolsBlock) > 0) {
      startAllPoolsSubstreams();
    }
  }, [getAllPoolsBlock]);

  //disable scroll updating input numbers
  document.addEventListener("wheel", function (_evnt) {
    const activeEl = document.activeElement;

    // Element is INPUT tag and if its type is number
    if (activeEl.tagName === "INPUT" && activeEl.type === "number") {
      activeEl.blur();
    }
  });

  return (
    <div className="App">
      <Router>
        <RouteChangeTracker />
        {!mobileScreen && (
          <React.Fragment>
            {/* <div style={{ display: "none" }} id="chart-renderer"></div> */}
            <CommonHeader
              setShowPopup={setShowPopup}
              setPopupContent={setPopupContent}
              setPopupContentPosition={setPopupContentPosition}
              setPageAction={setPageAction}
              favouriteClicked={favouriteClicked}
              setFavouriteClicked={setFavouriteClicked}
              setCustomLoading={setCustomLoading}
              generalDataProps={generalDataProps}
              verifiedWalletDetails={verifiedWalletDetails}
              custodyWalletDetails={custodyWalletDetails}
              setVerifiedWalletDetails={setVerifiedWalletDetails}
              mobileScreen={mobileScreen}
            />
            {showPopup && (
              <GeneralPopupBackground
                popupContent={popupContent}
                popupContentPosition={popupContentPosition}
                customLoading={customLoading}
                expandedView={expandedView}
              />
            )}

            <LayoutBody
              setShowPopup={setShowPopup}
              setPopupContent={setPopupContent}
              setPopupContentPosition={setPopupContentPosition}
              favouriteClicked={favouriteClicked}
              setFavouriteClicked={setFavouriteClicked}
              productsToSearch={productsToSearch}
              generalDataProps={generalDataProps}
              setCustomLoading={setCustomLoading}
              expandedView={expandedView}
              setExpandedView={setExpandedView}
            >
              <ToastContainer
                autoClose={5000}
                hideProgressBar
                pauseOnHover
                draggable
                closeOnClick
                closeButton={false}
                style={{
                  width: "100%",
                  position: "absolute",
                  left: "0",
                  top: "10px",
                  height: "67px",
                  maxHeight: "67px",
                  borderRadius: "8px",
                }}
              />
              <Suspense fallback={<div>{t("common.loading")}...</div>}>
                <Routes>
                  <Route
                    exact
                    path="/"
                    element={
                      <DashboardPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        showPopup={showPopup}
                        generalDataProps={generalDataProps}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                        setVerifiedWalletDetails={setVerifiedWalletDetails}
                        mobileScreen={false}
                        verifiedWalletDetails={verifiedWalletDetails}
                      />
                    }
                  />

                  <Route
                    exact
                    path={`/${tUrl("footer.investments")?.toLowerCase()}/${tUrl("investments.assets")?.toLowerCase()}`}
                    element={
                      <AssetsPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        generalDataProps={generalDataProps}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                      />
                    }
                  />

                  <Route
                    exact
                    path={`/${tUrl("footer.investments")?.toLowerCase()}/${tUrl("investments.assets")?.toLowerCase()}/:poolId`}
                    element={
                      <AssetsPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        generalDataProps={generalDataProps}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                        mobileScreen={mobileScreen}
                      />
                    }
                  />

                  <Route
                    exact
                    path={`/${tUrl("footer.dashboard")?.toLowerCase()}`}
                    element={
                      <DashboardPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        showPopup={showPopup}
                        generalDataProps={generalDataProps}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                        setVerifiedWalletDetails={setVerifiedWalletDetails}
                        mobileScreen={false}
                        verifiedWalletDetails={verifiedWalletDetails}
                      />
                    }
                  />
                  <Route
                    exact
                    path={`/${tUrl("footer.payments")?.toLowerCase()}/add-funds`}
                    element={
                      <DashboardPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        showPopup={showPopup}
                        generalDataProps={generalDataProps}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                        setVerifiedWalletDetails={setVerifiedWalletDetails}
                        mobileScreen={false}
                        verifiedWalletDetails={verifiedWalletDetails}
                      />
                    }
                  />

                  <Route
                    exact
                    path={`/${tUrl("footer.payments")?.toLowerCase()}/send-funds`}
                    element={
                      <DashboardPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        showPopup={showPopup}
                        generalDataProps={generalDataProps}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                        setVerifiedWalletDetails={setVerifiedWalletDetails}
                        mobileScreen={false}
                        verifiedWalletDetails={verifiedWalletDetails}
                      />
                    }
                  />

                  <Route
                    exact
                    path={`/${tUrl("footer.payments")?.toLowerCase()}/withdraw-funds`}
                    element={
                      <DashboardPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        showPopup={showPopup}
                        generalDataProps={generalDataProps}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                        setVerifiedWalletDetails={setVerifiedWalletDetails}
                        mobileScreen={false}
                        verifiedWalletDetails={verifiedWalletDetails}
                      />
                    }
                  />

                  <Route
                    exact
                    path="/blink/xe/"
                    element={
                      <DashboardPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        showPopup={showPopup}
                        generalDataProps={generalDataProps}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                        setVerifiedWalletDetails={setVerifiedWalletDetails}
                        mobileScreen={false}
                        verifiedWalletDetails={verifiedWalletDetails}
                      />
                    }
                  />

                  <Route
                    exact
                    path={`/${tUrl("footer.payments")?.toLowerCase()}/convert-funds`}
                    element={
                      <DashboardPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        showPopup={showPopup}
                        generalDataProps={generalDataProps}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                        setVerifiedWalletDetails={setVerifiedWalletDetails}
                        mobileScreen={false}
                        verifiedWalletDetails={verifiedWalletDetails}
                      />
                    }
                  />

                  <Route
                    exact
                    path={`/${tUrl("footer.investments")?.toLowerCase()}/${tUrl("investments.orders")?.toLowerCase()}`}
                    element={
                      <OrdersPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        generalDataProps={generalDataProps}
                      />
                    }
                  />
                  <Route
                    exact
                    path={`/${tUrl("footer.investments")?.toLowerCase()}/${tUrl("investments.portfolio")?.toLowerCase()}`}
                    element={
                      <PortfolioPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        generalDataProps={generalDataProps}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                      />
                    }
                  />

                  <Route
                    exact
                    path={`/${tUrl("footer.investments")?.toLowerCase()}/watchlists`}
                    element={
                      <WatchlistPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        generalDataProps={generalDataProps}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                      />
                    }
                  />

                  <Route
                    exact
                    path={`/${tUrl("footer.investments")?.toLowerCase()}/${tUrl("investments.assets")?.toLowerCase()}/create-secondary`}
                    element={
                      <CreateSecondary
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        generalDataProps={generalDataProps}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                      />
                    }
                  />
                  <Route
                    exact
                    path={`/${tUrl("footer.investments")?.toLowerCase()}/${tUrl("investments.portfolio")?.toLowerCase()}/create-secondary`}
                    element={
                      <CreateSecondary
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        generalDataProps={generalDataProps}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                      />
                    }
                  />
                  <Route
                    exact
                    path={`/${tUrl("footer.investments")?.toLowerCase()}/${tUrl("investments.assets")?.toLowerCase()}/create-secondary`}
                    element={
                      <CreateSecondary
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        generalDataProps={generalDataProps}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                      />
                    }
                  />
                  {/* <Route
                  exact
                  path="/new-issue"
                  element={
                    <NewIssue
                      setPopupContent={setPopupContent}
                      setShowPopup={setShowPopup}
                      setPopupContentPosition={setPopupContentPosition}
                      setFavouriteClicked={setFavouriteClicked}
                    />
                  }
                /> */}
                  <Route
                    exact
                    path={`/${tUrl("footer.dashboard")?.toLowerCase()}/new-issue`}
                    element={
                      <NewIssue
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        generalDataProps={generalDataProps}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                      />
                    }
                  />
                  <Route
                    exact
                    path={`/${tUrl("footer.investments")?.toLowerCase()}/${tUrl("investments.portfolio")?.toLowerCase()}/new-issue`}
                    element={
                      <NewIssue
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        generalDataProps={generalDataProps}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                      />
                    }
                  />
                  <Route
                    exact
                    path={`/${tUrl("footer.investments")?.toLowerCase()}/${tUrl("investments.portfolio")?.toLowerCase()}/corporate-actions`}
                    element={
                      <CoporateActions
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        setCustomLoading={setCustomLoading}
                        generalDataProps={generalDataProps}
                      />
                    }
                  />
                  <Route
                    exact
                    path={`/${tUrl("footer.investments")?.toLowerCase()}/${tUrl("investments.assets")?.toLowerCase()}/new-issue`}
                    element={
                      <NewIssue
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        generalDataProps={generalDataProps}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                      />
                    }
                  />

                  {process.env.REACT_APP_NODE_ENV !== "production" && (
                    <React.Fragment>
                      <Route
                        exact
                        path={`/${tUrl("footer.loans")?.toLowerCase()}/${tUrl("borrow.borrow")?.toLowerCase()}`}
                        element={
                          <LoansPage
                            setPopupContent={setPopupContent}
                            setShowPopup={setShowPopup}
                            setPopupContentPosition={setPopupContentPosition}
                            setFavouriteClicked={setFavouriteClicked}
                            generalDataProps={generalDataProps}
                            setCustomLoading={setCustomLoading}
                            expandedView={expandedView}
                            setExpandedView={setExpandedView}
                            loanAction="borrow"
                          />
                        }
                      />

                      <Route
                        exact
                        path={`/${tUrl("footer.loans")?.toLowerCase()}/lend`}
                        element={
                          <LoansPage
                            setPopupContent={setPopupContent}
                            setShowPopup={setShowPopup}
                            setPopupContentPosition={setPopupContentPosition}
                            setFavouriteClicked={setFavouriteClicked}
                            generalDataProps={generalDataProps}
                            setCustomLoading={setCustomLoading}
                            expandedView={expandedView}
                            setExpandedView={setExpandedView}
                            loanAction="lend"
                          />
                        }
                      />
                    </React.Fragment>
                  )}

                  <Route
                    exact
                    path="/accp-inv"
                    element={
                      <DashboardPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        showPopup={showPopup}
                        generalDataProps={generalDataProps}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                        setVerifiedWalletDetails={setVerifiedWalletDetails}
                        mobileScreen={false}
                        verifiedWalletDetails={verifiedWalletDetails}
                      />
                    }
                  />

                  <Route
                    exact
                    path="/sign-rec"
                    element={
                      <DashboardPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        showPopup={showPopup}
                        generalDataProps={generalDataProps}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                        setVerifiedWalletDetails={setVerifiedWalletDetails}
                        mobileScreen={false}
                        verifiedWalletDetails={verifiedWalletDetails}
                      />
                    }
                  />

                  <Route
                    exact
                    path="/acc-rec"
                    element={
                      <DashboardPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        showPopup={showPopup}
                        generalDataProps={generalDataProps}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                        setVerifiedWalletDetails={setVerifiedWalletDetails}
                        mobileScreen={false}
                        verifiedWalletDetails={verifiedWalletDetails}
                      />
                    }
                  />

                  <Route
                    exact
                    path="/create-passkey"
                    element={
                      <DashboardPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        showPopup={showPopup}
                        generalDataProps={generalDataProps}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                        setVerifiedWalletDetails={setVerifiedWalletDetails}
                        mobileScreen={false}
                        verifiedWalletDetails={verifiedWalletDetails}
                      />
                    }
                  />

                  <Route
                    exact
                    path="/get-passkey"
                    element={
                      <DashboardPage
                        setPopupContent={setPopupContent}
                        setShowPopup={setShowPopup}
                        setPopupContentPosition={setPopupContentPosition}
                        setFavouriteClicked={setFavouriteClicked}
                        showPopup={showPopup}
                        generalDataProps={generalDataProps}
                        customLoading={customLoading}
                        setCustomLoading={setCustomLoading}
                        expandedView={expandedView}
                        setExpandedView={setExpandedView}
                        setVerifiedWalletDetails={setVerifiedWalletDetails}
                        mobileScreen={false}
                        verifiedWalletDetails={verifiedWalletDetails}
                      />
                    }
                  />
                  {process.env.REACT_APP_NODE_ENV === "production" && (
                    <Route
                      exact
                      path="/wc"
                      element={
                        <DashboardPage
                          setPopupContent={setPopupContent}
                          setShowPopup={setShowPopup}
                          setPopupContentPosition={setPopupContentPosition}
                          setFavouriteClicked={setFavouriteClicked}
                          showPopup={showPopup}
                          generalDataProps={generalDataProps}
                          customLoading={customLoading}
                          setCustomLoading={setCustomLoading}
                          expandedView={expandedView}
                          setExpandedView={setExpandedView}
                          setVerifiedWalletDetails={setVerifiedWalletDetails}
                          mobileScreen={false}
                          verifiedWalletDetails={verifiedWalletDetails}
                        />
                      }
                    />
                  )}

                  <Route path="*" element={<ErrorPage />} />
                </Routes>
              </Suspense>
            </LayoutBody>
          </React.Fragment>
        )}
        {mobileScreen && (
          <React.Fragment>
            {!isFTU && (
              <CommonHeader
                mobileScreen={mobileScreen}
                generalDataProps={generalDataProps}
                verifiedWalletDetails={verifiedWalletDetails}
                setVerifiedWalletDetails={setVerifiedWalletDetails}
              />
            )}
            {showPopup && (
              <GeneralPopupBackground
                popupContent={popupContent}
                popupContentPosition={popupContentPosition}
                customLoading={customLoading}
                pageAction={pageAction}
              />
            )}
            <ToastContainer
              position="top-center"
              autoClose={3000}
              hideProgressBar
              pauseOnHover
              draggable
              closeOnClick
              closeButton={false}
              style={{
                width: "95%",
                height: "67px",
                maxHeight: "67px",
                position: "absolute",
                left: "2.5%",
                top: "25px",
                borderRadius: "8px",
              }}
            />
            <Suspense fallback={<div>{tUrl("common.loading")}...</div>}>
              <Routes>
                <Route
                  exact
                  path="/"
                  element={
                    <MobileDashboardPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      verifiedWalletDetails={verifiedWalletDetails}
                      setVerifiedWalletDetails={setVerifiedWalletDetails}
                    />
                  }
                />
                <Route
                  exact
                  path={`/${tUrl("footer.dashboard")?.toLowerCase()}`}
                  element={
                    <MobileDashboardPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      verifiedWalletDetails={verifiedWalletDetails}
                      setVerifiedWalletDetails={setVerifiedWalletDetails}
                    />
                  }
                />

                <Route
                  exact
                  path={`/${tUrl("footer.dashboard")?.toLowerCase()}/verify-kyc`}
                  element={
                    <MobileKYCPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      additionalProps={{
                        accessToken,
                        userAddress,
                        chainId,
                      }}
                    />
                  }
                />
                <Route
                  exact
                  path="/verify-kyc"
                  element={
                    <MobileKYCPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      additionalProps={{
                        accessToken,
                        userAddress,
                        chainId,
                      }}
                    />
                  }
                />

                <Route
                  exact
                  path={`/${tUrl("footer.payments")?.toLowerCase()}`}
                  element={
                    <MobilePaymentsPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                    />
                  }
                />
                <Route
                  exact
                  path={`/${tUrl("footer.investments")?.toLowerCase()}`}
                  element={
                    <MobileAssetPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      productsToSearch={productsToSearch}
                    />
                  }
                />

                <Route
                  exact
                  path={`/${tUrl("footer.investments")?.toLowerCase()}/${tUrl("investments.assets")?.toLowerCase()}/:poolId`}
                  element={
                    <MobileAssetPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      productsToSearch={productsToSearch}
                      setPageAction={setPageAction}
                    />
                  }
                />

                <Route
                  exact
                  path="/profile"
                  element={
                    <MobileProfilePage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      productsToSearch={productsToSearch}
                      verifiedWalletDetails={verifiedWalletDetails}
                      setVerifiedWalletDetails={setVerifiedWalletDetails}
                    />
                  }
                />
                <Route
                  exact
                  path="/profile-details"
                  element={
                    <MobileProfileDetailsPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      productsToSearch={productsToSearch}
                    />
                  }
                />
                <Route
                  exact
                  path="/profile-settings"
                  element={
                    <MobileProfileSettingsPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      setPageAction={setPageAction}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      productsToSearch={productsToSearch}
                      verifiedWalletDetails={verifiedWalletDetails}
                      custodyWalletDetails={custodyWalletDetails}
                    />
                  }
                />
                <Route
                  exact
                  path="/add-funds"
                  element={
                    <MobileAddFundsPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      productsToSearch={productsToSearch}
                    />
                  }
                />
                <Route
                  exact
                  path="/add-funds-method"
                  element={
                    <MobileAddFundsPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      showPaymentMethod={true}
                      productsToSearch={productsToSearch}
                    />
                  }
                />
                <Route
                  exact
                  path="/add-funds/currencies"
                  element={
                    <MobileSelectCurrencies
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      baseUrl="add-funds"
                      productsToSearch={productsToSearch}
                    />
                  }
                />
                <Route
                  exact
                  path="/send-funds"
                  element={
                    <MobileSendFundsPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      showPopup={showPopup}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      customLoading={customLoading}
                      setCustomLoading={setCustomLoading}
                      baseUrl="send-funds"
                      productsToSearch={productsToSearch}
                    />
                  }
                />
                <Route
                  exact
                  path="/convert-funds/currencies"
                  element={
                    <MobileSelectCurrencies
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      baseUrl="convert-funds"
                      productsToSearch={productsToSearch}
                    />
                  }
                />
                <Route
                  exact
                  path="/convert-funds"
                  element={
                    <MobileConvertFunds
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      productsToSearch={productsToSearch}
                    />
                  }
                />
                <Route
                  exact
                  path="/withdraw-funds"
                  element={
                    <MobileWithdrawFundsPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      productsToSearch={productsToSearch}
                    />
                  }
                />
                <Route
                  exact
                  path="/withdraw-funds-method"
                  element={
                    <MobileWithdrawFundsPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      productsToSearch={productsToSearch}
                      showPaymentMethod={true}
                    />
                  }
                />
                <Route
                  exact
                  path="/withdraw-funds/currencies"
                  element={
                    <MobileSelectCurrencies
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      baseUrl="withdraw-funds"
                      productsToSearch={productsToSearch}
                    />
                  }
                />
                <Route
                  exact
                  path="/link-bank-option"
                  element={
                    <MobileLinkBankOption
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                    />
                  }
                />

                <Route
                  exact
                  path="/link-bank"
                  element={
                    <MobileLinkBank
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                    />
                  }
                />

                <Route
                  exact
                  path="/select-bank"
                  element={
                    <MobileSelectBank
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                    />
                  }
                />

                <Route
                  exact
                  path="/loading"
                  element={
                    <MobileLoadingPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                    />
                  }
                />

                <Route
                  exact
                  path="/reloading"
                  element={
                    <MobileLoadingPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                    />
                  }
                />

                <Route
                  exact
                  path="/asset-info"
                  element={
                    <MobileAssetInfoPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      productsToSearch={productsToSearch}
                    />
                  }
                />

                <Route
                  exact
                  path="/accp-inv"
                  element={
                    <MobileDashboardPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      verifiedWalletDetails={verifiedWalletDetails}
                      setVerifiedWalletDetails={setVerifiedWalletDetails}
                    />
                  }
                />

                <Route
                  exact
                  path="/sign-rec"
                  element={
                    <MobileDashboardPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      verifiedWalletDetails={verifiedWalletDetails}
                      setVerifiedWalletDetails={setVerifiedWalletDetails}
                    />
                  }
                />

                <Route
                  exact
                  path="/acc-rec"
                  element={
                    <MobileDashboardPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      verifiedWalletDetails={verifiedWalletDetails}
                      setVerifiedWalletDetails={setVerifiedWalletDetails}
                    />
                  }
                />

                <Route
                  exact
                  path="/create-passkey"
                  element={
                    <MobileDashboardPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      verifiedWalletDetails={verifiedWalletDetails}
                      setVerifiedWalletDetails={setVerifiedWalletDetails}
                    />
                  }
                />

                <Route
                  exact
                  path="/get-passkey"
                  element={
                    <MobileDashboardPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      verifiedWalletDetails={verifiedWalletDetails}
                      setVerifiedWalletDetails={setVerifiedWalletDetails}
                    />
                  }
                />

                <Route
                  exact
                  path={`/${tUrl("footer.loans")?.toLowerCase()}`}
                  element={
                    <MobileLoansPage
                      mobileScreen={mobileScreen}
                      generalDataProps={generalDataProps}
                      setShowPopup={setShowPopup}
                      setPopupContent={setPopupContent}
                      setPopupContentPosition={setPopupContentPosition}
                      favouriteClicked={favouriteClicked}
                      setFavouriteClicked={setFavouriteClicked}
                      setCustomLoading={setCustomLoading}
                      verifiedWalletDetails={verifiedWalletDetails}
                    />
                  }
                />

                <Route
                  path="*"
                  element={<ErrorPage mobileScreen={mobileScreen} />}
                />
              </Routes>
            </Suspense>
            <div style={{ height: "50px" }}></div>

            {!isFTU && (
              <CommonFooter
                mobileScreen={mobileScreen}
                generalDataProps={generalDataProps}
                setShowPopup={setShowPopup}
                setPopupContent={setPopupContent}
                setPopupContentPosition={setPopupContentPosition}
                favouriteClicked={favouriteClicked}
                setFavouriteClicked={setFavouriteClicked}
                setCustomLoading={setCustomLoading}
              />
            )}
          </React.Fragment>
        )}
      </Router>
    </div>
  );
}

export default App;
